import {
  Button,
  ButtonGroup,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  OdinIcon,
} from '@myosh/odin-components';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BowtieDto as SaveBowtieDto } from '../../api/generated/bowtie-api';
import type { BowtieDto } from '../../api/generated/v4-api';
import { useAppPermissionsContext } from '../../context/app-permissions.context';
import { makeDataCy } from '../../helpers/util';
import useCreateBackendRecords from '../../hooks/use-create-backend-records-button-action';
import useDeleteDiagram from '../../hooks/use-delete-diagram-button-action';
import useSaveDiagram from '../../hooks/use-save-diagram-button-action';

interface CreateSplitButtonProps {
  bowtieDtoCreate?: BowtieDto;
  bowtieDtoSave?: SaveBowtieDto;
  mueLabel?: string;
  mueRecordId?: number;
}

const CreateSplitButton = ({ bowtieDtoCreate, bowtieDtoSave, mueLabel, mueRecordId }: CreateSplitButtonProps) => {
  const {
    loading: isSavingOrUpdating,
    label: saveOrUpdateButtonLabel,
    handleClick: handleSaveOrUpdateClick,
    diagramId,
  } = useSaveDiagram(bowtieDtoSave, mueLabel);

  // generate slug for fallback button selector
  const dataCyLabel = useMemo(() => makeDataCy(saveOrUpdateButtonLabel), [saveOrUpdateButtonLabel]);

  const { handleClick: handleCreateBackedRecordsClick, disabled: isCreateBackedRecordsDisabled } =
    useCreateBackendRecords(bowtieDtoCreate, mueLabel, mueRecordId);

  const { loading: isDeleting, handleDelete } = useDeleteDiagram(diagramId);

  const { t } = useTranslation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDeleteClick = useCallback(() => setConfirmingDelete(true), []);

  const handleDeleteDialogClose = useCallback(() => setConfirmingDelete(false), []);

  const { canCreateBackendRecordsPermission } = useAppPermissionsContext();

  const canDelete = Boolean(diagramId);
  const canRenderSplitButton = canCreateBackendRecordsPermission || canDelete;

  const disabled = isSavingOrUpdating || isCreateBackedRecordsDisabled || isDeleting;

  if (!canRenderSplitButton) {
    return (
      <Button size="lg" data-cy={`${dataCyLabel}-button`} onClick={handleSaveOrUpdateClick} disabled={disabled}>
        <OdinIcon icon="Save" data-icon="inline-start" />
        {saveOrUpdateButtonLabel}
      </Button>
    );
  }

  return (
    <>
      <ButtonGroup data-cy="split-button" disabled={disabled}>
        <Button size="lg" onClick={handleSaveOrUpdateClick}>
          <OdinIcon icon="Save" data-icon="inline-start" />
          {saveOrUpdateButtonLabel}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button size="icon-lg" aria-label="More save options" />}>
            <OdinIcon icon="ChevronDown" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canCreateBackendRecordsPermission && (
              <DropdownMenuItem onClick={handleCreateBackedRecordsClick}>
                <OdinIcon icon="SquarePlus" />
                Create Backend Records
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem onClick={handleDeleteClick}>
                <OdinIcon icon="Trash" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
      <Dialog
        visible={confirmingDelete}
        header={t('delete-modal-header')}
        description={t('confirm-delete-diagram')}
        buttons={[
          {
            name: 'delete',
            text: t('delete'),
            variant: 'destructive',
            closeModalOnClick: true,
            onClick: () => void handleDelete(),
          },
        ]}
        onClose={handleDeleteDialogClose}
      />
    </>
  );
};

export default memo(CreateSplitButton);
